import * as i1 from '@angular/common/http';
import * as i0 from '@angular/core';
import { Injectable, InjectionToken, Inject, ViewContainerRef, Component, ViewChild, Input, ViewEncapsulation, ChangeDetectionStrategy, APP_INITIALIZER, NgModule } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import '@alfresco/js-api';

/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// eslint-disable-next-line no-shadow
var ContentActionType;
(function (ContentActionType) {
    ContentActionType["default"] = "default";
    ContentActionType["button"] = "button";
    ContentActionType["separator"] = "separator";
    ContentActionType["menu"] = "menu";
    ContentActionType["custom"] = "custom";
})(ContentActionType || (ContentActionType = {}));

/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const getValue = (target, key) => {
    if (!target) {
        return undefined;
    }
    const keys = key.split('.');
    key = '';
    do {
        key += keys.shift();
        const value = target[key];
        if (value !== undefined &&
            (typeof value === 'object' || !keys.length)) {
            target = value;
            key = '';
        }
        else if (!keys.length) {
            target = undefined;
        }
        else {
            key += '.';
        }
    } while (keys.length);
    return target;
};
const filterEnabled = (entry) => !entry.disabled;
const sortByOrder = (a, b) => {
    const left = a.order === undefined ? Number.MAX_SAFE_INTEGER : a.order;
    const right = b.order === undefined ? Number.MAX_SAFE_INTEGER : b.order;
    return left - right;
};
const reduceSeparators = (acc, el, i, arr) => {
    // remove leading separator
    if (i === 0) {
        if (arr[i].type === ContentActionType.separator) {
            return acc;
        }
    }
    // remove duplicate separators
    if (i > 0) {
        const prev = arr[i - 1];
        if (prev.type === ContentActionType.separator &&
            el.type === ContentActionType.separator) {
            return acc;
        }
        // remove trailing separator
        if (i === arr.length - 1) {
            if (el.type === ContentActionType.separator) {
                return acc;
            }
        }
    }
    return acc.concat(el);
};
const reduceEmptyMenus = (acc, el) => {
    if (el.type === ContentActionType.menu) {
        if ((el.children || []).length === 0) {
            return acc;
        }
    }
    return acc.concat(el);
};
const mergeObjects = (...objects) => {
    const result = {};
    objects.forEach((source) => {
        Object.keys(source).forEach((prop) => {
            let replace = false;
            if (prop.endsWith('.$replace')) {
                replace = true;
                prop = prop.replace('.$replace', '');
            }
            if (!prop.startsWith('$')) {
                if (replace) {
                    result[prop] = source[`${prop}.$replace`];
                }
                else if (prop in result && Array.isArray(result[prop])) {
                    if (Array.isArray(source[prop])) {
                        result[prop] = mergeArrays(result[prop], source[prop]);
                    }
                    else {
                        result[prop] = [...result[prop], source[prop]];
                    }
                }
                else if (prop in result && typeof result[prop] === 'object') {
                    result[prop] = mergeObjects(result[prop], source[prop]);
                }
                else {
                    result[prop] = source[prop];
                }
            }
        });
    });
    return result;
};
const mergeArrays = (left, right) => {
    const result = [];
    const map = {};
    (left || []).forEach((entry) => {
        const element = entry;
        if (element && Object.prototype.hasOwnProperty.call(element, 'id')) {
            map[element.id] = element;
        }
        else {
            result.push(element);
        }
    });
    (right || []).forEach((entry) => {
        const element = entry;
        if (element && Object.prototype.hasOwnProperty.call(element, 'id') && map[element.id]) {
            const merged = mergeObjects(map[element.id], element);
            map[element.id] = merged;
        }
        else {
            result.push(element);
        }
    });
    return Object.keys(map).map((key) => map[key]).concat(result);
};

/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
class RuleRef {
}

/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
class ExtensionLoaderService {
    constructor(http) {
        this.http = http;
    }
    load(configPath, pluginsPath, extensions, extensionValues) {
        return new Promise((resolve) => {
            this.loadConfig(configPath, 0).then((result) => {
                if (result) {
                    let config = result.config;
                    const override = sessionStorage.getItem('app.extension.config');
                    if (override) {
                        config = JSON.parse(override);
                    }
                    if (!config.$references?.length) {
                        config.$references = this.filterIgnoredExtensions(extensions || [], config.$ignoreReferenceList);
                    }
                    else {
                        config.$references = this.filterIgnoredExtensions(config.$references, config.$ignoreReferenceList);
                    }
                    if (config.$references?.length > 0 || extensionValues) {
                        const plugins = (config.$references ?? []).map((name, idx) => this.loadConfig(`${pluginsPath}/${name}`, idx));
                        Promise.all(plugins).then((results) => {
                            let configs = results
                                .filter((entry) => entry)
                                .sort(sortByOrder)
                                .map((entry) => entry.config);
                            if (extensionValues) {
                                configs = [
                                    ...configs,
                                    ...extensionValues
                                ];
                            }
                            if (configs.length > 0) {
                                config = mergeObjects(config, ...configs);
                            }
                            config = {
                                ...config,
                                ...this.getMetadata(result.config),
                                $references: configs.map((ext) => this.getMetadata(ext))
                            };
                            resolve(config);
                        });
                    }
                    else {
                        resolve(config);
                    }
                }
            });
        });
    }
    /**
     * Retrieves configuration elements.
     * Filters element by **enabled** and **order** attributes.
     * Example:
     *  `getElements<ViewerExtensionRef>(config, 'features.viewer.extensions')`
     *
     * @param config configuration settings
     * @param key element key
     * @param fallback fallback array of values
     * @returns list of elements
     */
    getElements(config, key, fallback = []) {
        const values = getValue(config, key) || fallback || [];
        return values.filter(filterEnabled).sort(sortByOrder);
    }
    getContentActions(config, key) {
        return this.getElements(config, key).map(this.setActionDefaults);
    }
    getRules(config) {
        if (config?.rules) {
            return config.rules;
        }
        return [];
    }
    getRoutes(config) {
        if (config) {
            return config.routes || [];
        }
        return [];
    }
    getActions(config) {
        if (config) {
            return config.actions || [];
        }
        return [];
    }
    getFeatures(config) {
        if (config) {
            return config.features || [];
        }
        return [];
    }
    getMetadata(config) {
        const result = {};
        Object
            .keys(config)
            .filter((key) => key.startsWith('$'))
            .forEach((key) => {
            result[key] = config[key];
        });
        return result;
    }
    loadConfig(url, order) {
        return new Promise((resolve) => {
            this.http.get(url).subscribe((config) => {
                resolve({
                    order,
                    config
                });
            }, () => {
                resolve(null);
            });
        });
    }
    setActionDefaults(action) {
        if (action) {
            action.type = action.type || ContentActionType.default;
            action.icon = action.icon || 'extension';
        }
        return action;
    }
    filterIgnoredExtensions(extensions, ignoreReferenceList) {
        if (!ignoreReferenceList?.length) {
            return extensions;
        }
        return extensions.map((file) => file.match('(?!.*/).+')[0]).filter((fileName) => !ignoreReferenceList.includes(fileName));
    }
}
ExtensionLoaderService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ExtensionLoaderService, deps: [{ token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
ExtensionLoaderService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ExtensionLoaderService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ExtensionLoaderService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }]; } });

/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const not = (context, ...args) => {
    if (!args || args.length === 0) {
        return false;
    }
    return args
        .every((arg) => {
        const evaluator = context.getEvaluator(arg.value);
        if (!evaluator) {
            console.warn('evaluator not found: ' + arg.value);
            return false;
        }
        return !evaluator(context, ...(arg.parameters || []));
    });
};
const every = (context, ...args) => {
    if (!args || args.length === 0) {
        return false;
    }
    return args
        .every((arg) => {
        const evaluator = context.getEvaluator(arg.value);
        if (!evaluator) {
            console.warn('evaluator not found: ' + arg.value);
            return false;
        }
        return evaluator(context, ...(arg.parameters || []));
    });
};
const some = (context, ...args) => {
    if (!args || args.length === 0) {
        return false;
    }
    return args
        .some((arg) => {
        const evaluator = context.getEvaluator(arg.value);
        if (!evaluator) {
            console.warn('evaluator not found: ' + arg.value);
            return false;
        }
        return evaluator(context, ...(arg.parameters || []));
    });
};

/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
class ComponentRegisterService {
    constructor() {
        this.components = {};
    }
    setComponents(values) {
        if (values) {
            this.components = Object.assign({}, this.components, values);
        }
    }
    getComponentById(id) {
        return this.components[id];
    }
    hasComponentById(id) {
        return !!this.getComponentById(id);
    }
}
ComponentRegisterService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ComponentRegisterService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
ComponentRegisterService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ComponentRegisterService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ComponentRegisterService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
class RuleService {
    constructor(loader) {
        this.loader = loader;
        this.context = null;
        this.rules = [];
        this.evaluators = {};
    }
    setup(config) {
        this.rules = this.loader.getRules(config);
    }
    /**
     * Adds one or more new rule evaluators to the existing set.
     *
     * @param values The new evaluators to add
     */
    setEvaluators(values) {
        if (values) {
            this.evaluators = Object.assign({}, this.evaluators, values);
        }
    }
    /**
     * Retrieves a rule using its ID value.
     *
     * @param id The ID value to look for
     * @returns The rule or null if not found
     */
    getRuleById(id) {
        return this.rules.find((ref) => ref.id === id);
    }
    /**
     * Retrieves a RuleEvaluator function using its key name.
     *
     * @param key Key name to look for
     * @returns RuleEvaluator or null if not found
     */
    getEvaluator(key) {
        if (key?.startsWith('!')) {
            const fn = this.evaluators[key.substring(1)];
            return (context, ...args) => !fn(context, ...args);
        }
        return this.evaluators[key];
    }
    /**
     * Evaluates a rule.
     *
     * @param ruleId ID of the rule to evaluate
     * @param context Custom rule execution context.
     * @returns True if the rule passed, false otherwise
     */
    evaluateRule(ruleId, context) {
        const ruleRef = this.getRuleById(ruleId);
        context = context || this.context;
        if (ruleRef) {
            const evaluator = this.getEvaluator(ruleRef.type);
            if (evaluator) {
                return evaluator(context, ...ruleRef.parameters);
            }
        }
        else {
            const evaluator = this.getEvaluator(ruleId);
            if (evaluator) {
                return evaluator(context);
            }
        }
        return false;
    }
}
RuleService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: RuleService, deps: [{ token: ExtensionLoaderService }], target: i0.ɵɵFactoryTarget.Injectable });
RuleService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: RuleService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: RuleService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: ExtensionLoaderService }]; } });

/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
/**
 * The default extensions factory
 *
 * @returns the list of extension json files
 */
function extensionJsonsFactory() {
    return [];
}
;
const EXTENSION_JSONS = new InjectionToken('extension-jsons', {
    providedIn: 'root',
    factory: extensionJsonsFactory
});
const EXTENSION_JSON_VALUES = new InjectionToken('extension-jsons-values', {
    providedIn: 'root',
    factory: extensionJsonsFactory
});
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
/**
 * Provides the extension json values for the angular modules
 *
 * @param jsons files to provide
 * @returns a provider section
 */
function provideExtensionConfig(jsons) {
    return {
        provide: EXTENSION_JSONS,
        useValue: jsons,
        multi: true
    };
}
;
/**
 * Provides the extension json raw values for the angular modules
 *
 * @param extensionConfigValue config value
 * @returns a provider section
 */
function provideExtensionConfigValues(extensionConfigValue) {
    return {
        provide: EXTENSION_JSON_VALUES,
        useValue: extensionConfigValue,
        multi: true
    };
}
;
class ExtensionService {
    constructor(loader, componentRegister, ruleService, extensionJsons, extensionJsonValues) {
        this.loader = loader;
        this.componentRegister = componentRegister;
        this.ruleService = ruleService;
        this.extensionJsons = extensionJsons;
        this.extensionJsonValues = extensionJsonValues;
        this.configPath = 'assets/app.extensions.json';
        this.pluginsPath = 'assets/plugins';
        this.routes = [];
        this.actions = [];
        this.features = [];
        this.authGuards = {};
        this.config = null;
        this.onSetup$ = new BehaviorSubject(this.config);
        this.setup$ = this.onSetup$.asObservable();
    }
    /**
     * Loads and registers an extension config file and plugins (specified by path properties).
     *
     * @returns The loaded config data
     */
    async load() {
        const config = await this.loader.load(this.configPath, this.pluginsPath, this.extensionJsons.flat(), this.extensionJsonValues.flat());
        this.setup(config);
        return config;
    }
    /**
     * Registers extensions from a config object.
     *
     * @param config Object with config data
     */
    setup(config) {
        if (!config) {
            console.warn('Extension configuration not found');
            return;
        }
        this.config = config;
        this.setEvaluators({
            'core.every': every,
            'core.some': some,
            'core.not': not
        });
        this.actions = this.loader.getActions(config);
        this.routes = this.loader.getRoutes(config);
        this.features = this.loader.getFeatures(config);
        this.ruleService.setup(config);
        this.onSetup$.next(config);
    }
    /**
     * Gets features by key.
     *
     * @param key Key string using dot notation or array of strings
     * @param defaultValue Default value returned if feature is not found, default is empty array
     * @returns Feature found by key
     */
    getFeature(key, defaultValue = []) {
        const properties = Array.isArray(key) ? key : key.split('.');
        return properties.reduce((prev, curr) => prev?.[curr], this.features) || defaultValue;
    }
    getElements(key, fallback = []) {
        return this.loader.getElements(this.config, key, fallback);
    }
    /**
     * Adds one or more new rule evaluators to the existing set.
     *
     * @param values The new evaluators to add
     */
    setEvaluators(values) {
        this.ruleService.setEvaluators(values);
    }
    /**
     * Adds one or more new auth guards to the existing set.
     *
     * @param values The new auth guards to add
     */
    setAuthGuards(values) {
        if (values) {
            this.authGuards = Object.assign({}, this.authGuards, values);
        }
    }
    /**
     * Adds one or more new components to the existing set.
     *
     * @param values The new components to add
     */
    setComponents(values) {
        this.componentRegister.setComponents(values);
    }
    /**
     * Retrieves a route using its ID value.
     *
     * @param id The ID value to look for
     * @returns The route or null if not found
     */
    getRouteById(id) {
        return this.routes.find((route) => route.id === id);
    }
    /**
     * Retrieves one or more auth guards using an array of ID values.
     *
     * @param ids Array of ID value to look for
     * @returns Array of auth guards or empty array if none were found
     */
    getAuthGuards(ids) {
        return (ids || [])
            .map((id) => this.authGuards[id])
            .filter((guard) => guard);
    }
    /**
     * Retrieves an action using its ID value.
     *
     * @param id The ID value to look for
     * @returns Action or null if not found
     */
    getActionById(id) {
        return this.actions.find((action) => action.id === id);
    }
    /**
     * Retrieves a RuleEvaluator function using its key name.
     *
     * @param key Key name to look for
     * @returns RuleEvaluator or null if not found
     */
    getEvaluator(key) {
        return this.ruleService.getEvaluator(key);
    }
    /**
     * Evaluates a rule.
     *
     * @param ruleId ID of the rule to evaluate
     * @param context Custom rule execution context.
     * @returns True if the rule passed, false otherwise
     */
    evaluateRule(ruleId, context) {
        return this.ruleService.evaluateRule(ruleId, context);
    }
    /**
     * Retrieves a registered extension component using its ID value.
     *
     * @param id The ID value to look for
     * @returns The component or null if not found
     */
    getComponentById(id) {
        return this.componentRegister.getComponentById(id);
    }
    /**
     * Retrieves a rule using its ID value.
     *
     * @param id The ID value to look for
     * @returns The rule or null if not found
     */
    getRuleById(id) {
        return this.ruleService.getRuleById(id);
    }
    /**
     * Runs a lightweight expression stored in a string.
     *
     * @param value String containing the expression or literal value
     * @param context Parameter object for the expression with details of app state
     * @returns Result of evaluated expression, if found, or the literal value otherwise
     */
    runExpression(value, context) {
        if (typeof value === 'string') {
            return this.evaluateExpression(value, context);
        }
        else {
            const duplicate = Object.assign({}, value);
            Object.keys(duplicate).forEach((key) => {
                duplicate[key] = this.evaluateExpression(duplicate[key], context);
            });
            return duplicate;
        }
    }
    evaluateExpression(value, context) {
        const pattern = new RegExp(/\$\((.*\)?)\)/g);
        const matches = pattern.exec(value);
        if (matches && matches.length > 1) {
            const expression = matches[1];
            const fn = new Function('context', `return ${expression}`);
            const result = fn(context);
            return result;
        }
        return value;
    }
}
ExtensionService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ExtensionService, deps: [{ token: ExtensionLoaderService }, { token: ComponentRegisterService }, { token: RuleService }, { token: EXTENSION_JSONS }, { token: EXTENSION_JSON_VALUES }], target: i0.ɵɵFactoryTarget.Injectable });
ExtensionService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ExtensionService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ExtensionService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: ExtensionLoaderService }, { type: ComponentRegisterService }, { type: RuleService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [EXTENSION_JSONS]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [EXTENSION_JSON_VALUES]
                }] }]; } });

/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
class AppExtensionService {
    constructor(extensionService) {
        this.extensionService = extensionService;
        this._references = new BehaviorSubject([]);
        this.references$ = this._references.asObservable();
    }
    async load() {
        const config = await this.extensionService.load();
        this.setup(config);
    }
    setup(config) {
        if (!config) {
            return;
        }
        const references = (config.$references || [])
            .filter((entry) => typeof entry === 'object')
            .map((entry) => entry);
        this._references.next(references);
    }
    /**
     * Provides a collection of document list columns for the particular preset.
     * The result is filtered by the **disabled** state.
     *
     * @param key Preset key.
     * @returns list of document list presets
     */
    getDocumentListPreset(key) {
        return this.extensionService
            .getElements(`features.documentList.${key}`)
            .filter((entry) => !entry.disabled);
    }
    /**
     * Provides a list of the Viewer content extensions,
     * filtered by **disabled** state and **rules**.
     *
     * @returns list of viewer extension references
     */
    getViewerExtensions() {
        return this.extensionService
            .getElements('features.viewer.extensions')
            .filter((extension) => !this.isViewerExtensionDisabled(extension));
    }
    isViewerExtensionDisabled(extension) {
        if (extension) {
            if (extension.disabled) {
                return true;
            }
            if (extension.rules?.disabled) {
                return this.extensionService.evaluateRule(extension.rules.disabled);
            }
        }
        return false;
    }
}
AppExtensionService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AppExtensionService, deps: [{ token: ExtensionService }], target: i0.ɵɵFactoryTarget.Injectable });
AppExtensionService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AppExtensionService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AppExtensionService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: ExtensionService }]; } });

/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
class PreviewExtensionComponent {
    constructor(extensionService) {
        this.extensionService = extensionService;
        /** Identifier of a node that is opened by the viewer. */
        this.nodeId = null;
    }
    ngOnInit() {
        if (!this.id) {
            return;
        }
        const componentType = this.extensionService.getComponentById(this.id);
        if (componentType) {
            this.content.clear();
            this.componentRef = this.content.createComponent(componentType, { index: 0 });
            this.updateInstance();
        }
    }
    ngOnChanges() {
        this.updateInstance();
    }
    ngOnDestroy() {
        if (this.componentRef) {
            this.componentRef.destroy();
            this.componentRef = null;
        }
    }
    updateInstance() {
        if (this.componentRef?.instance) {
            const instance = this.componentRef.instance;
            instance.url = this.url;
            instance.extension = this.extension;
            instance.nodeId = this.nodeId;
        }
    }
}
PreviewExtensionComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PreviewExtensionComponent, deps: [{ token: ExtensionService }], target: i0.ɵɵFactoryTarget.Component });
PreviewExtensionComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: PreviewExtensionComponent, isStandalone: true, selector: "adf-preview-extension", inputs: { id: "id", nodeId: "nodeId", url: "url", extension: "extension" }, viewQueries: [{ propertyName: "content", first: true, predicate: ["content"], descendants: true, read: ViewContainerRef, static: true }], usesOnChanges: true, ngImport: i0, template: `<div #content></div>`, isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PreviewExtensionComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'adf-preview-extension',
                    standalone: true,
                    template: `<div #content></div>`
                }]
        }], ctorParameters: function () { return [{ type: ExtensionService }]; }, propDecorators: { content: [{
                type: ViewChild,
                args: ['content', { read: ViewContainerRef, static: true }]
            }], id: [{
                type: Input
            }], nodeId: [{
                type: Input
            }], url: [{
                type: Input
            }], extension: [{
                type: Input
            }] } });

/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
class DynamicColumnComponent {
    constructor(extensions) {
        this.extensions = extensions;
    }
    ngOnInit() {
        const componentType = this.extensions.getComponentById(this.id);
        if (componentType) {
            this.content.clear();
            this.componentRef = this.content.createComponent(componentType, { index: 0 });
            this.updateInstance();
        }
    }
    ngOnChanges(changes) {
        if (changes.node) {
            this.updateInstance();
        }
    }
    ngOnDestroy() {
        if (this.componentRef) {
            this.componentRef.destroy();
            this.componentRef = null;
        }
    }
    updateInstance() {
        if (this.componentRef?.instance) {
            this.componentRef.instance.context = this.context;
        }
    }
}
DynamicColumnComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: DynamicColumnComponent, deps: [{ token: ExtensionService }], target: i0.ɵɵFactoryTarget.Component });
DynamicColumnComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: DynamicColumnComponent, isStandalone: true, selector: "adf-dynamic-column", inputs: { id: "id", context: "context" }, host: { classAttribute: "adf-dynamic-column" }, viewQueries: [{ propertyName: "content", first: true, predicate: ["content"], descendants: true, read: ViewContainerRef, static: true }], usesOnChanges: true, ngImport: i0, template: `<ng-container #content></ng-container>`, isInline: true, styles: [".adf-dynamic-column{display:flex;align-items:center;width:inherit}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: DynamicColumnComponent, decorators: [{
            type: Component,
            args: [{ selector: 'adf-dynamic-column', standalone: true, template: `<ng-container #content></ng-container>`, encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, host: { class: 'adf-dynamic-column' }, styles: [".adf-dynamic-column{display:flex;align-items:center;width:inherit}\n"] }]
        }], ctorParameters: function () { return [{ type: ExtensionService }]; }, propDecorators: { content: [{
                type: ViewChild,
                args: ['content', { read: ViewContainerRef, static: true }]
            }], id: [{
                type: Input
            }], context: [{
                type: Input
            }] } });

/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// cSpell:words lifecycle
class DynamicExtensionComponent {
    constructor(extensions) {
        this.extensions = extensions;
        this.loaded = false;
    }
    ngOnChanges(changes) {
        if (!this.loaded) {
            this.loadComponent();
            this.loaded = true;
        }
        if (changes.data) {
            this.data = changes.data.currentValue;
        }
        this.updateInstance();
        this.proxy('ngOnChanges', changes);
    }
    ngOnDestroy() {
        if (this.componentCreated()) {
            this.componentRef.destroy();
            this.componentRef = null;
        }
    }
    loadComponent() {
        const componentType = this.extensions.getComponentById(this.id);
        if (componentType) {
            this.content.clear();
            this.componentRef = this.content.createComponent(componentType, { index: 0 });
        }
    }
    updateInstance() {
        if (this.componentCreated()) {
            this.componentRef.instance.data = this.data;
        }
    }
    proxy(lifecycleMethod, ...args) {
        if (this.componentCreated() && this.lifecycleHookIsImplemented(lifecycleMethod)) {
            // eslint-disable-next-line prefer-spread
            this.componentRef.instance[lifecycleMethod].apply(this.componentRef.instance, args);
        }
    }
    componentCreated() {
        return !!this.componentRef && !!this.componentRef.instance;
    }
    lifecycleHookIsImplemented(lifecycleMethod) {
        return !!this.componentRef.instance[lifecycleMethod];
    }
}
DynamicExtensionComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: DynamicExtensionComponent, deps: [{ token: ExtensionService }], target: i0.ɵɵFactoryTarget.Component });
DynamicExtensionComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: DynamicExtensionComponent, isStandalone: true, selector: "adf-dynamic-component", inputs: { id: "id", data: "data" }, viewQueries: [{ propertyName: "content", first: true, predicate: ["content"], descendants: true, read: ViewContainerRef, static: true }], usesOnChanges: true, ngImport: i0, template: `<div #content></div>`, isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: DynamicExtensionComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'adf-dynamic-component',
                    standalone: true,
                    template: `<div #content></div>`
                }]
        }], ctorParameters: function () { return [{ type: ExtensionService }]; }, propDecorators: { content: [{
                type: ViewChild,
                args: ['content', { read: ViewContainerRef, static: true }]
            }], id: [{
                type: Input
            }], data: [{
                type: Input
            }] } });

/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
class DynamicTabComponent {
    constructor(extensions) {
        this.extensions = extensions;
    }
    ngOnInit() {
        const componentType = this.extensions.getComponentById(this.id);
        if (componentType) {
            this.content.clear();
            this.componentRef = this.content.createComponent(componentType, { index: 0 });
            this.updateInstance();
        }
    }
    ngOnChanges(changes) {
        if (changes.node) {
            this.updateInstance();
        }
    }
    ngOnDestroy() {
        if (this.componentRef) {
            this.componentRef.destroy();
            this.componentRef = null;
        }
    }
    updateInstance() {
        if (this.componentRef?.instance) {
            this.componentRef.instance.node = this.node;
        }
    }
}
DynamicTabComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: DynamicTabComponent, deps: [{ token: ExtensionService }], target: i0.ɵɵFactoryTarget.Component });
DynamicTabComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.10", type: DynamicTabComponent, isStandalone: true, selector: "adf-dynamic-tab", inputs: { id: "id", node: "node" }, viewQueries: [{ propertyName: "content", first: true, predicate: ["content"], descendants: true, read: ViewContainerRef, static: true }], usesOnChanges: true, ngImport: i0, template: `<div #content></div>`, isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: DynamicTabComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'adf-dynamic-tab',
                    standalone: true,
                    template: `<div #content></div>`
                }]
        }], ctorParameters: function () { return [{ type: ExtensionService }]; }, propDecorators: { content: [{
                type: ViewChild,
                args: ['content', { read: ViewContainerRef, static: true }]
            }], id: [{
                type: Input
            }], node: [{
                type: Input
            }] } });

/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const setupExtensions = (appExtensionService) => () => appExtensionService.load();

/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const EXTENSION_DIRECTIVES = [DynamicExtensionComponent, DynamicTabComponent, DynamicColumnComponent, PreviewExtensionComponent];
/** @deprecated import EXTENSION_DIRECTIVES or standalone components instead */
class ExtensionsModule {
    static forRoot() {
        return {
            ngModule: ExtensionsModule,
            providers: [
                {
                    provide: APP_INITIALIZER,
                    useFactory: setupExtensions,
                    deps: [AppExtensionService],
                    multi: true
                }
            ]
        };
    }
    /**
     * @deprecated use `ExtensionsModule` instead, `EXTENSION_DIRECTIVES` or direct standalone components
     * @returns Module with providers
     */
    static forChild() {
        return {
            ngModule: ExtensionsModule
        };
    }
}
ExtensionsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ExtensionsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
ExtensionsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: ExtensionsModule, imports: [DynamicExtensionComponent, DynamicTabComponent, DynamicColumnComponent, PreviewExtensionComponent], exports: [DynamicExtensionComponent, DynamicTabComponent, DynamicColumnComponent, PreviewExtensionComponent] });
ExtensionsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ExtensionsModule, imports: [EXTENSION_DIRECTIVES] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ExtensionsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [...EXTENSION_DIRECTIVES],
                    exports: [...EXTENSION_DIRECTIVES]
                }]
        }] });

/*!
 * @license
 * Copyright © 2005-2024 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AppExtensionService, ComponentRegisterService, ContentActionType, DynamicColumnComponent, DynamicExtensionComponent, DynamicTabComponent, EXTENSION_DIRECTIVES, EXTENSION_JSONS, EXTENSION_JSON_VALUES, ExtensionLoaderService, ExtensionService, ExtensionsModule, PreviewExtensionComponent, RuleRef, RuleService, extensionJsonsFactory, filterEnabled, getValue, mergeArrays, mergeObjects, provideExtensionConfig, provideExtensionConfigValues, reduceEmptyMenus, reduceSeparators, sortByOrder };
